import type { QueryClient } from '@tanstack/react-query';
import type { i18n as I18n } from 'i18next';
import { createUploadManager, type UploadManager } from '../features/studio/upload/uploadManager.ts';
import { type ApiClient, createApiClient } from '../shared/api/client.ts';
import { createFetchTransport } from '../shared/api/http.ts';
import { createQueryClient } from '../shared/api/queryClient.ts';
import { queryKeys } from '../shared/api/queryKeys.ts';
import { createI18n } from '../shared/i18n/setup.ts';
import { createAppRouter } from './router.tsx';

/** Budget for ordinary JSON calls; uploads and long operations set their own. */
const API_TIMEOUT_MS = 20_000;

export interface AppServices {
  readonly api: ApiClient;
  readonly queryClient: QueryClient;
  readonly i18n: I18n;
  readonly uploads: UploadManager;
  readonly router: ReturnType<typeof createAppRouter>;
}

/** The composition root: every long-lived dependency of the SPA is created here, once. */
export const createAppServices = (): AppServices => {
  const api = createApiClient(createFetchTransport({ timeoutMs: API_TIMEOUT_MS }));
  const queryClient = createQueryClient();
  const uploads = createUploadManager({
    api,
    onAssetsChanged: () => void queryClient.invalidateQueries({ queryKey: queryKeys.assets.all() }),
  });
  return {
    api,
    queryClient,
    i18n: createI18n(),
    uploads,
    router: createAppRouter(),
  };
};
